/**
 * The calendar month a request counts in, said in Swedish. Build round A2, 2026-10-08.
 *
 * Every month the cap is read against arrives from the database as a date, `YYYY-MM-01`
 * (`requests.counts_in_month`, the `month` of both usage reads), already decided in
 * Europe/Stockholm. It is a calendar date and not a moment, so it is read from its digits and never
 * through `new Date(...)`: `new Date("2026-11-01")` is midnight UTC, which a browser west of
 * Greenwich formats as the 31st of October and so as the wrong month. A fixed list of names also
 * keeps the word the same whatever locale data the browser ships.
 */

const MONTHS = [
  "januari",
  "februari",
  "mars",
  "april",
  "maj",
  "juni",
  "juli",
  "augusti",
  "september",
  "oktober",
  "november",
  "december"
] as const;

const CALENDAR_MONTH = /^(\d{4})-(\d{2})/;

/** The month's index, 0 to 11, read from the date's digits; null for anything that is not a date. */
function monthIndex(date: string | null | undefined): number | null {
  const match = date ? CALENDAR_MONTH.exec(date) : null;
  if (!match) {
    return null;
  }
  const index = Number(match[2]) - 1;
  return index >= 0 && index < 12 ? index : null;
}

/** "oktober" for `2026-10-01`. Lower case, as Swedish writes a month inside a sentence. */
export function monthWord(date: string | null | undefined): string | null {
  const index = monthIndex(date);
  return index === null ? null : MONTHS[index];
}

/** "november" for `2026-10-01`, and "januari" for a December. */
export function nextMonthWord(date: string | null | undefined): string | null {
  const index = monthIndex(date);
  return index === null ? null : MONTHS[(index + 1) % 12];
}

/** The word with a capital first letter, for a month that opens a sentence: "Oktober: 3 av 3". */
export function capitalised(word: string): string {
  return word.charAt(0).toUpperCase() + word.slice(1);
}
